import React, { useEffect, useMemo, useState } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"

const LESSON_GROUPS = [
  "present",
  "past",
  "future",
  "articles",
  "agreement",
  "prepositions",
  "comparatives",
  "conditionals",
  "modals",
  "reported",
  "passives",
]

const PRACTICE_GROUPS = [...LESSON_GROUPS, "mastery"]

function topicKind(item) {
  return item?.kind || (item?.group === "mastery" ? "exercise" : "lesson")
}

function isLesson(item) {
  return topicKind(item) === "lesson"
}

function groupLabel(group, t) {
  return t(`grammarGroup_${group}`) || group
}

function formHeaders(topic, t) {
  if (Array.isArray(topic?.form_headers) && topic.form_headers.length === 4) {
    return topic.form_headers
  }
  return [t("grammarPerson"), t("grammarPositive"), t("grammarNegative"), t("grammarQuestion")]
}

function recommendedTopic(topics, tab) {
  const ready = topics.filter((item) => item.ready)
  if (tab === "learn") {
    const lessons = ready.filter(isLesson)
    return lessons.find((item) => !item.completed) || lessons[0] || null
  }
  const started = ready.some((item) => item.attempts > 0)
  const openCore = ready.find((item) => !item.completed && item.group !== "mastery")
  const openMix = ready.find((item) => !item.completed && item.group === "mastery")
  if (!started) {
    return ready.find((item) => item.id === "present_simple") || openCore || openMix || null
  }
  return openCore || openMix || null
}

function groupTopics(list, order) {
  const byGroup = {}
  list.forEach((item) => {
    const key = item.group || "other"
    if (!byGroup[key]) byGroup[key] = []
    byGroup[key].push(item)
  })
  const extra = Object.keys(byGroup).filter((key) => !order.includes(key))
  return [...order, ...extra]
    .filter((group) => byGroup[group]?.length)
    .map((group) => ({ group, items: byGroup[group] }))
}

function GrammarRule({ topic, t }) {
  if (!topic) return null
  return (
    <>
      {topic.explanation ? <p className="grammar-explain">{topic.explanation}</p> : null}
      {topic.form?.length ? (
        <div className="grammar-form-wrap">
          <table className="grammar-form">
            <thead>
              <tr>
                {formHeaders(topic, t).map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topic.form.map((row) => (
                <tr key={row.person}>
                  <td>{row.person}</td>
                  <td>{row.positive}</td>
                  <td>{row.negative}</td>
                  <td>{row.question}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {topic.examples?.length ? (
        <ul className="grammar-examples">
          {topic.examples.map((example) => (
            <li key={example.sentence}>
              <strong>{example.sentence}</strong>
              {example.note ? <span>{example.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )
}

function TopicCard({ item, t, onOpen }) {
  if (!item.ready) {
    return (
      <div className="grammar-topic-card is-soon">
        <strong>{item.title}</strong>
        <span>{item.summary}</span>
        <em>{t("grammarComingSoon")}</em>
      </div>
    )
  }
  return (
    <button type="button" className="grammar-topic-card" onClick={() => onOpen(item)}>
      <strong>
        {item.title}
        {item.mastered ? (
          <span className="grammar-badge">{t("grammarMastered")}</span>
        ) : item.completed ? (
          <span className="grammar-badge">{t("grammarCompleted")}</span>
        ) : null}
      </strong>
      <span>{item.summary}</span>
      <em>
        {item.difficulty}
        {item.item_count
          ? ` · ${item.item_count} ${t("grammarItems")}`
          : ` · ${t("grammarLearn")}`}
        {item.attempts > 0 && item.best_percent != null
          ? ` · ${t("grammarBest")} ${Math.round(item.best_percent)}%`
          : null}
      </em>
    </button>
  )
}

export default function Grammar() {
  const { t } = useLanguage()
  const [topics, setTopics] = useState([])
  const [summary, setSummary] = useState({ completed: 0, mastered: 0, total: 0 })
  const [listTab, setListTab] = useState("learn")
  const [topic, setTopic] = useState(null)
  const [mode, setMode] = useState("learn")
  const [showRule, setShowRule] = useState(false)
  const [answers, setAnswers] = useState({})
  const [result, setResult] = useState(null)
  const [missedIds, setMissedIds] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const loadTopics = () =>
    api.grammarTopics().then((data) => {
      setTopics(data.topics || [])
      setSummary({
        completed: data.completed ?? data.mastered ?? 0,
        mastered: data.mastered || 0,
        total: data.total || (data.topics || []).filter((item) => item.ready).length,
      })
      return data
    })

  useEffect(() => {
    let cancelled = false
    setError("")
    loadTopics()
      .catch((exc) => {
        if (!cancelled) setError(exc.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const learnTopics = useMemo(() => topics.filter(isLesson), [topics])
  const practiceTopics = useMemo(() => topics.filter((item) => item.ready || item.item_count), [topics])
  const listTopics = listTab === "learn" ? learnTopics : practiceTopics
  const grouped = useMemo(
    () => groupTopics(listTopics, listTab === "learn" ? LESSON_GROUPS : PRACTICE_GROUPS),
    [listTopics, listTab]
  )

  const nextUp = useMemo(() => recommendedTopic(listTopics, listTab), [listTopics, listTab])

  const nextTopicAfterCurrent = useMemo(() => {
    if (!topic) return null
    const pool = listTopics.filter((item) => item.ready && item.id !== topic.id)
    const extras = pool.filter(
      (item) =>
        (item.lesson_id && (item.lesson_id === topic.id || item.lesson_id === topic.lesson_id)) ||
        item.id === topic.lesson_id
    )
    const extraOpen = extras.find((item) => !item.completed)
    if (listTab === "practice" && extraOpen) return extraOpen
    const readyInTab = listTopics.filter((item) => item.ready)
    const index = readyInTab.findIndex((item) => item.id === topic.id)
    return (
      readyInTab.slice(index + 1).find((item) => !item.completed) ||
      pool.find((item) => !item.completed) ||
      null
    )
  }, [listTopics, topic, listTab])

  const openTopic = async (item, nextMode) => {
    const id = typeof item === "string" ? item : item?.id
    if (!id) return
    const resolvedMode =
      nextMode ||
      (listTab === "practice" || topicKind(item) === "exercise" ? "practice" : "learn")
    setError("")
    setResult(null)
    setAnswers({})
    setMissedIds(null)
    setShowRule(resolvedMode === "learn")
    setLoading(true)
    try {
      const data = await api.grammarTopic(id)
      setTopic({ ...data, kind: data.kind || topicKind(item) })
      setMode(resolvedMode)
    } catch (exc) {
      setError(exc.message)
    } finally {
      setLoading(false)
    }
  }

  const startExercises = async () => {
    if (!topic) return
    if (topic.items?.length) {
      setMode("practice")
      setShowRule(false)
      setResult(null)
      setMissedIds(null)
      return
    }
    const extra = topics.find(
      (item) => item.ready && item.lesson_id === topic.id && item.id !== topic.id
    )
    if (extra) {
      setListTab("practice")
      await openTopic(extra, "practice")
    }
  }

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
  }

  const checkAnswers = async (event) => {
    event.preventDefault()
    if (!topic || submitting) return
    setSubmitting(true)
    setError("")
    try {
      const scored = await api.completeGrammarTopic(topic.id, answers)
      setResult(scored)
      setMissedIds(null)
      loadTopics().catch(() => {})
    } catch (exc) {
      setError(exc.message)
    } finally {
      setSubmitting(false)
    }
  }

  const retryMissed = () => {
    const missed = result?.missed_ids || (result?.results || []).filter((row) => !row.correct).map((row) => row.id)
    if (!missed.length) return
    setMissedIds(missed)
    setAnswers((prev) => {
      const kept = { ...prev }
      missed.forEach((id) => {
        delete kept[id]
      })
      return kept
    })
    setResult(null)
  }

  const backToList = () => {
    setTopic(null)
    setMode("learn")
    setShowRule(false)
    setResult(null)
    setAnswers({})
    setMissedIds(null)
    setError("")
  }

  const resultById = useMemo(() => {
    const map = {}
    for (const row of result?.results || []) map[row.id] = row
    return map
  }, [result])

  const visibleItems = useMemo(() => {
    const items = topic?.items || []
    if (!missedIds?.length) return items
    return items.filter((item) => missedIds.includes(item.id))
  }, [topic, missedIds])

  const lessonTotals = useMemo(() => {
    const ready = learnTopics.filter((item) => item.ready)
    return {
      completed: ready.filter((item) => item.completed).length,
      total: ready.length,
    }
  }, [learnTopics])

  const practiceTotals = useMemo(() => {
    const ready = practiceTopics.filter((item) => item.ready)
    return {
      completed: ready.filter((item) => item.completed).length,
      total: ready.length,
    }
  }, [practiceTopics])

  if (loading && !topic && topics.length === 0) {
    return (
      <div className="grammar-page">
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    )
  }

  return (
    <div className="grammar-page">
      <header className="vocab-hero">
        <h1>{t("grammar")}</h1>
        <p>{t("grammarPageLead")}</p>
        {summary.total > 0 ? (
          <p className="grammar-progress-meta">
            {t("grammarMasteryCount")
              .replace("{n}", String(summary.completed ?? summary.mastered ?? 0))
              .replace("{total}", String(summary.total))}
          </p>
        ) : null}
      </header>

      {error ? (
        <p className="vocab-inline-error" role="alert">
          {error}
        </p>
      ) : null}

      {!topic ? (
        <>
          <div className="grammar-tabs" role="tablist" aria-label={t("grammar")}>
            <button
              type="button"
              role="tab"
              aria-selected={listTab === "learn"}
              className={`grammar-tab${listTab === "learn" ? " is-active" : ""}`}
              onClick={() => setListTab("learn")}
            >
              {t("grammarLearn")}
              {lessonTotals.total ? ` · ${lessonTotals.completed}/${lessonTotals.total}` : null}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={listTab === "practice"}
              className={`grammar-tab${listTab === "practice" ? " is-active" : ""}`}
              onClick={() => setListTab("practice")}
            >
              {t("grammarPractice")}
              {practiceTotals.total ? ` · ${practiceTotals.completed}/${practiceTotals.total}` : null}
            </button>
          </div>
          <p className="grammar-tab-lead">
            {listTab === "learn" ? t("grammarLearnLead") : t("grammarPracticeLead")}
          </p>
          {nextUp ? (
            <section className="grammar-recommended">
              <p className="grammar-recommended-label">{t("grammarRecommended")}</p>
              <button
                type="button"
                className="grammar-topic-card grammar-topic-card--next"
                onClick={() => openTopic(nextUp, listTab === "practice" ? "practice" : "learn")}
              >
                <strong>{nextUp.title}</strong>
                <span>{nextUp.summary}</span>
                <em>
                  {nextUp.difficulty}
                  {nextUp.item_count ? ` · ${nextUp.item_count} ${t("grammarItems")}` : null}
                </em>
              </button>
            </section>
          ) : null}
          {grouped.map(({ group, items }) => (
            <section key={group} className="grammar-group">
              <h2>{groupLabel(group, t)}</h2>
              <ul className="grammar-topic-list">
                {items.map((item) => (
                  <li key={item.id}>
                    <TopicCard
                      item={item}
                      t={t}
                      onOpen={(card) => openTopic(card, listTab === "practice" ? "practice" : "learn")}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      ) : mode === "learn" ? (
        <section className="grammar-practice">
          <button type="button" className="vocab-remove" onClick={backToList}>
            {t("grammarBackToTopics")}
          </button>
          <p className="grammar-mode-label">{t("grammarLearn")}</p>
          <h2>{topic.title}</h2>
          <GrammarRule topic={topic} t={t} />
          <div className="grammar-actions">
            <button type="button" className="vocab-btn vocab-btn--primary" onClick={startExercises}>
              {t("grammarStartExercises")}
            </button>
          </div>
        </section>
      ) : (
        <section className="grammar-practice">
          <button type="button" className="vocab-remove" onClick={backToList}>
            {t("grammarBackToTopics")}
          </button>
          <p className="grammar-mode-label">{t("grammarPractice")}</p>
          <h2>{topic.title}</h2>
          {topic.explanation || topic.form?.length ? (
            <button
              type="button"
              className="grammar-rule-toggle"
              onClick={() => setShowRule((open) => !open)}
            >
              {showRule ? t("grammarHideRule") : t("grammarReviewRule")}
            </button>
          ) : null}
          {showRule ? <GrammarRule topic={topic} t={t} /> : null}

          {result ? (
            <p className="vocab-inline-ok" role="status">
              {t("grammarScore")}: {result.score}/{result.total}
              {result.mastered ? ` · ${t("grammarMastered")}` : null}
            </p>
          ) : missedIds?.length ? (
            <p className="grammar-hint">{t("grammarRetryMissedHint")}</p>
          ) : null}

          <form className="grammar-items" onSubmit={checkAnswers}>
            {visibleItems.map((item, index) => {
              const scored = resultById[item.id]
              const answerLabel = Array.isArray(scored?.answers) ? scored.answers[0] : ""
              return (
                <fieldset key={item.id} className={scored ? (scored.correct ? "is-correct" : "is-wrong") : undefined}>
                  <legend>
                    {index + 1}. {item.prompt}
                  </legend>
                  {item.type === "multiple_choice" ? (
                    <ul className="grammar-options">
                      {(item.options || []).map((option) => (
                        <li key={option}>
                          <label>
                            <input
                              type="radio"
                              name={item.id}
                              value={option}
                              disabled={Boolean(result)}
                              checked={(answers[item.id] || "") === option}
                              onChange={() => setAnswer(item.id, option)}
                            />
                            {option}
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <input
                      type="text"
                      className="vocab-search"
                      value={answers[item.id] || ""}
                      disabled={Boolean(result)}
                      onChange={(event) => setAnswer(item.id, event.target.value)}
                      autoComplete="off"
                      aria-label={item.prompt}
                    />
                  )}
                  {item.hint && !result ? <p className="grammar-hint">{t("grammarHint")}: {item.hint}</p> : null}
                  {scored && !scored.correct && answerLabel ? (
                    <p className="grammar-answer">
                      {t("grammarAnswer")}: {answerLabel}
                    </p>
                  ) : null}
                  {scored?.explanation ? (
                    <p className="grammar-why">
                      {t("grammarExplanation")}: {scored.explanation}
                    </p>
                  ) : null}
                </fieldset>
              )
            })}
            {result ? (
              <div className="grammar-actions">
                {result.missed_ids?.length ? (
                  <button type="button" className="vocab-btn vocab-btn--primary" onClick={retryMissed}>
                    {t("grammarRetryMissed")}
                  </button>
                ) : null}
                <button type="button" className="vocab-btn" onClick={() => openTopic(topic.id, "practice")}>
                  {t("grammarTryAgain")}
                </button>
                {nextTopicAfterCurrent ? (
                  <button
                    type="button"
                    className="vocab-btn"
                    onClick={() =>
                      openTopic(
                        nextTopicAfterCurrent,
                        listTab === "learn" && isLesson(nextTopicAfterCurrent) ? "learn" : "practice"
                      )
                    }
                  >
                    {t("grammarNextTopic")}: {nextTopicAfterCurrent.title}
                  </button>
                ) : null}
              </div>
            ) : (
              <button type="submit" className="vocab-btn vocab-btn--primary" disabled={submitting}>
                {submitting ? t("loading") : t("grammarCheck")}
              </button>
            )}
          </form>
        </section>
      )}
    </div>
  )
}
